import {
	EventService,
	GamedayApiService,
	GameService,
	SeasonService
} from "@prestosports/prestostats-core/Services";
import { EventStatus, SportCodes } from "@prestosports/prestostats-core/Common";
import {
	CLEAR_GAME,
	INIT_GAME_DATA,
	INIT_GAME_DATA_COMPLETE
} from "./constants";
import GamedayApiClient from "../../services/gameday-api-client";
import { overWriteEventData, setCurrentEvent } from "../events/actions";
import { createGameContext, validateGameData } from "../game-context/actions";
import { initTeamsContext } from "../team-context/actions";
import {
	getCsvHeaders,
	initFootballPlayers,
	initTennisPlayers
} from "../players/actions";
import { initSportData } from "../sport/actions";
import {
	initPlaysData,
	resetInsert as resetInsertAction,
	startPlay
} from "../football-play/actions";
import { GameRoutes } from "../../routes/game-routes";
import { CommonRoutes } from "../../routes/common-routes";
import { getGameAccess, withSync } from "../app/actions";
import { ON_ERROR } from "../app/constants";
import { getStreams, toggleStreamOpen } from "../stream/actions";

GamedayApiService.setGamedayApiClient(GamedayApiClient);

export const initCommonGameData = async (season, event, game, dispatch) => {
	return Promise.all([
		initTeamsContext(game, event.teams, dispatch),
		initSportData(season.sport.sportId, dispatch),
		getGameAccess(game.id, dispatch),
		getStreams(event.id, dispatch),
		toggleStreamOpen(false, event.id, dispatch)
	]);
};

export const initFootballData = async (
	season,
	event,
	game,
	gameContext,
	dispatch
) => {
	await initFootballPlayers(event.teams, dispatch);
	await Promise.all([
		initCommonGameData(season, event, game, dispatch),
		initPlaysData(game, dispatch),
		startPlay(game.id, dispatch)
	]);

	if (gameContext) {
		await createGameContext(gameContext, dispatch);
		validateGameData(game.id, dispatch);
		getGameAccess(game.id, dispatch);
	}

	await resetInsertAction(game.id, dispatch);
};

export const initTennisData = async (
	season,
	event,
	game,
	gameContext,
	dispatch
) => {
	await initTennisPlayers(event.teams, dispatch);
	await initCommonGameData(season, event, game, dispatch);

	if (gameContext) {
		await createGameContext(gameContext, dispatch);
	}
};

const sportInitializers = {
	[SportCodes.FlagFootball]: initFootballData,
	[SportCodes.Football]: initFootballData,
	[SportCodes.Tennis]: initTennisData,
	[SportCodes.WomenTennis]: initTennisData
};

const playersInitialzers = {
	[SportCodes.FlagFootball]: initFootballPlayers,
	[SportCodes.Football]: initFootballPlayers,
	[SportCodes.Tennis]: initTennisPlayers,
	[SportCodes.WomenTennis]: initTennisPlayers
};

export const setCurrentPlayers = async (teams, sportId, dispatch) => {
	return playersInitialzers[sportId](teams, dispatch);
};

export const initGameDataFromExternalEventId = async (
	externalEventId,
	redirect
) => {
	if (externalEventId) {
		const currentEvent = await EventService.getByExternalId(externalEventId);
		const { id } = currentEvent;

		if (EventStatus.isFinal(currentEvent)) {
			redirect(GameRoutes.STATS.url.replace(":eventId", id));
		} else if (!EventStatus.isStarted(currentEvent)) {
			redirect(
				`${GameRoutes.EVENT_INFORMATION.url.replace(":eventId", id)}#settings`
			);
		} else {
			redirect(GameRoutes.GAME_MODE.url.replace(":eventId", id));
		}
	}
};

export const loadGameData = async (eventId, dispatch) => {
	dispatch({
		type: INIT_GAME_DATA
	});

	const { game, gameContext } = await GameService.getOrCreateGame(eventId);
	const currentEvent = await setCurrentEvent(eventId, dispatch);
	const season = await SeasonService.getBySeasonId(currentEvent.seasonId);
	await getCsvHeaders(eventId, dispatch);

	if (season && game && sportInitializers[season.sport.sportId]) {
		await sportInitializers[season.sport.sportId](
			season,
			currentEvent,
			game,
			gameContext,
			dispatch
		);
	}

	dispatch({
		type: INIT_GAME_DATA_COMPLETE,
		payload: {
			game: game ? game.toJSON() : null
		}
	});
};

export const initGameData = async (eventId, redirect, dispatch) => {
	if (!eventId) {
		redirect(CommonRoutes.NOT_FOUND.url);
	} else {
		return withSync(
			eventId,
			async () => {
				await loadGameData(eventId, dispatch);
			},
			async () => {
				await overWriteEventData(eventId, dispatch);
			},
			dispatch
		);
	}

	return null;
};

export const clearGame = (dispatch) => {
	dispatch({
		type: CLEAR_GAME,
		payload: {
			game: null
		}
	});
};

export const gameError = (dispatch) => {
	dispatch({
		type: ON_ERROR
	});
};
